import { ReactNode, useMemo } from 'react';

import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { ClientPresentationDesignerV2_EntityFragment } from '@/modules/presentation/clientPresentation/graphql/ClientPresentationDesignerV2.generated';

import { EditEntitySectionButton } from '../../../presentationEntityActions/PresentationEntityActionButtons';
import { usePresentationEntityActionsContext } from '../../../presentationEntityActions/presentationEntityActions.context';
import {
  getDispositionsEditLabel,
  resolveAccessAndDispositionsSections,
} from './accessAndDispositionsSections';

interface UseBeneficiariesDispositionsToolbarsParams {
  entity: ClientPresentationDesignerV2_EntityFragment;
  /** When false (e.g. a view-only viewer), no toolbars are returned. */
  canEdit: boolean;
}

/**
 * Builds the "Edit current access" and "Edit dispositions" toolbars for the
 * beneficiaries-and-dispositions slide from a plain entity, so both the
 * presentation designer and the single-entity export preview can offer the same
 * per-card edit affordances. Returns `undefined` toolbars when editing is off.
 */
export function useBeneficiariesDispositionsToolbars({
  entity,
  canEdit,
}: UseBeneficiariesDispositionsToolbarsParams): {
  currentAccessToolbar: ReactNode;
  dispositionsToolbar: ReactNode;
} {
  const rowActions = usePresentationEntityActionsContext();
  const canEditEntity = canEdit && !!rowActions;
  const entityType = getEntityTypeFromEntityKind(entity.kind);

  // Resolve the target sections during render rather than inside the memo:
  // `getEditEntitySections` reflects the BDG feature flag (which resolves the
  // dispositions card to beneficiary designations vs dispositive provisions), so
  // resolving here keeps the buttons correct as flags settle. The results are
  // primitive enums, so the toolbar memo below stays referentially stable when
  // nothing actually changes.
  const { currentAccessSection, dispositionsSection } = canEditEntity
    ? resolveAccessAndDispositionsSections(
        rowActions.getEditEntitySections(entityType)
      )
    : { currentAccessSection: undefined, dispositionsSection: undefined };

  return useMemo<{
    currentAccessToolbar: ReactNode;
    dispositionsToolbar: ReactNode;
  }>(() => {
    const target = { entityId: entity.id, entityType };
    return {
      currentAccessToolbar: currentAccessSection ? (
        <EditEntitySectionButton
          target={target}
          section={currentAccessSection}
          label="Edit current access"
        />
      ) : undefined,
      dispositionsToolbar: dispositionsSection ? (
        <EditEntitySectionButton
          target={target}
          section={dispositionsSection}
          label={getDispositionsEditLabel(dispositionsSection)}
        />
      ) : undefined,
    };
  }, [currentAccessSection, dispositionsSection, entity.id, entityType]);
}
